const getDefaultState = () => {
  return {
    /* CORE ROOM META INFO */
    room: null,
    sessionID: null,

    /* LOCAL PARTICIPANT DATA */
    localParticipantData: null,
    localParticipantScreenData: null,
    localVideoTrack: null,
    localAudioTrack: null,

    localTrack: false,
    localScreenTrack: null,

    /* REMOTE PARTICIPANT DATA */
    remoteParticipantData: null,
    remoteParticipantScreenData: null,
    remoteAudioTrack: null,
    partnerAudio: null /* We start off with true, to avoid showing '.partnerCameraOff' div, e.g. we assume it will be on */,
    partnerVideo: null /* We start off with true, to avoid showing '.partnerCameraOff' div, e.g. we assume it will be on  */,
    partnerScreen: false,
    partnerStatesInitialized: false,

    /* THEME */
    themeCoreColor: "#2d2d2d",
    themeBorderColor: "transparent",
    themeLightBackgroundColor: "#fff",

    /* SESSION PREFERENCES */
    sessionPreferences: null,

    /* DEVICES */
    current: {
      audioinput: "", // microphone
      videoinput: "", // camera
      audiooutput: "", // speaker
    },
  };
};

const state = getDefaultState();

// Synchronous
const mutations = {
  /* CORE ROOM STATE */
  RESET_ROOM(state) {
    Object.assign(state, getDefaultState());
  },

  /* CORE ROOM META INFO */
  SET_ROOM_DETAILS(state, roomDetails) {
    state.room = roomDetails.room;
    state.sessionID = roomDetails.sessionID;
  },
  SET_ROOM(state, room) {
    state.room = room;
  },
  SET_SESSION_ID(state, sessionID) {
    state.sessionID = sessionID;
  },

  /* LOCAL PARTICIPANT DATA */
  SET_LOCAL_PARTICIPANT_DATA(state, data) {
    state.localParticipantData = data.localParticipantData;
    state.localVideoTrack = data.localVideoTrack;
    state.localAudioTrack = data.localAudioTrack;
  },

  SET_LOCAL_PARTICIPANT_SCREEN_DATA(state, data) {
    state.localParticipantScreenData = data;
  },

  SET_LOCAL_VIDEO_TRACK(state, data) {
    state.localVideoTrack = data;
  },

  SET_LOCAL_AUDIO_TRACK(state, data) {
    state.localAudioTrack = data;
  },

  /* REMOTE PARTICIPANT DATA */
  SET_REMOTE_PARTICIPANT_DATA(state, data) {
    state.remoteParticipantData = data;
  },

  SET_REMOTE_PARTICIPANT_SCREEN_DATA(state, data) {
    state.remoteParticipantScreenData = data;
  },

  SET_REMOTE_AUDIO_TRACK(state, data) {
    state.remoteAudioTrack = data;
  },

  /**
   * @param {Object} data 
   * @param {String} data.mediaType - 'partnerAudio' or 'partnerVideo'
   */
  SET_REMOTE_MEDIA_STATUS(state, data) {
    state[data.mediaType] = data.newStatus;

    /* Used to avoid showing '.partnerCameraOffText' text div while still loading in RemoteParticipant.vue */
    /* if (!state.partnerStatesInitialized) state.partnerStatesInitialized = true; */
  },

  RESET_REMOTE_PARTICIPANT_DATA(state) {
    state.remoteParticipantData = null;
    state.remoteParticipantScreenData = null;
    state.partnerAudio = null;
    state.partnerVideo = null;
    /* state.partnerStatesInitialized = false; */
    state.partnerScreen = false;
  },

  SET_REMOTE_STATES_READY(state) {
    /* Used to avoid showing '.partnerCameraOffText' text div while still loading in RemoteParticipant.vue but also to show it when no tracks available at all */
    if (!state.partnerStatesInitialized) state.partnerStatesInitialized = true;

    /** Initially, partnerAudio and partnerVideo states are set as true, so that the icons would look neutral and not
     * flash back and forth. During registering existing or future remote participants, it is determined whether any
     * of their media is turned off. However, if it's turned off while the remote participant enters, then there is
     * actually no track to look at. Which is why we additionally, explicitly set the tracks as false *IF* the remote
     * participant did not have either an audio or video track. */
    if (!state.remoteParticipantData || !state.remoteParticipantData.videoTracks || !state.remoteParticipantData.videoTracks.size)
      state.partnerVideo = false;
    if (!state.remoteParticipantData || !state.remoteParticipantData.audioTracks || !state.remoteParticipantData.audioTracks.size)
      state.partnerAudio = false;
  },

  SET_REMOTE_PARTNER_MEDIA_FALSE(state, mediaType) {
    state[mediaType] = false;
  },

  /* SESSION PREFERENCES */
  SET_SESSION_PREFERENCES(state, sessionPreferences) {
    state.sessionPreferences = sessionPreferences;
  },

  UPDATE_PREFERENCE_FIELD(state, updateData) {
    console.log("@UPDATE_PREFERENCE_FIELD: ", updateData);
    const field = updateData.field;
    const newValue = updateData.newValue;
    state.sessionPreferences[field] = newValue;
  },

  /* DEVICES */
  SET_CURRENT_DEVICE(state, data) {
    console.log("@SET_CURRENT_DEVICE: ", data);
    console.log("device kind: ", data.kind);
    const device = {
      deviceId: data.deviceId,
      kind: data.kind,
      label: data.label,
    };
    state.current[device.kind] = device;
  },
};

// Asynchronous
const actions = {
  /* CORE ROOM STATE */
  resetRoom({ commit }) {
    commit("RESET_ROOM");
  },

  /* CORE ROOM META INFO */
  setRoomDetails({ commit }, roomDetails) {
    commit("SET_ROOM_DETAILS", roomDetails);
  },
  setRoom({ commit }, room) {
    commit("SET_ROOM", room);
  },
  setSessionID({ commit }, sessionID) {
    commit("SET_SESSION_ID", sessionID);
  },

  /* LOCAL PARTICIPANT DATA */
  setLocalParticipantData({ commit }, data) {
    commit("SET_LOCAL_PARTICIPANT_DATA", data);
  },

  setLocalParticipantScreenData({ commit }, data) {
    commit("SET_LOCAL_PARTICIPANT_SCREEN_DATA", data);
  },

  setLocalVideoTrack({ commit }, data) {
    commit("SET_LOCAL_VIDEO_TRACK", data);
  },

  setLocalAudioTrack({ commit }, data) {
    commit("SET_LOCAL_AUDIO_TRACK", data);
  },

  /* REMOTE PARTICIPANT DATA */
  setRemoteParticipantData({ commit }, data) {
    commit("SET_REMOTE_PARTICIPANT_DATA", data);
  },

  setRemoteParticipantScreenData({ commit }, data) {
    commit("SET_REMOTE_PARTICIPANT_SCREEN_DATA", data);
  },

  setRemoteAudioTrack({ commit }, data) {
    commit("SET_REMOTE_AUDIO_TRACK", data);
  },

  setRemoteMediaStatus({ commit }, data) {
    commit("SET_REMOTE_MEDIA_STATUS", data);
  },

  resetRemoteParticipantData({ commit }) {
    commit("RESET_REMOTE_PARTICIPANT_DATA");
  },

  setRemoteStatesReady({ commit }) {
    commit("SET_REMOTE_STATES_READY");
  },

  setRemotePartnerMediaFalse({ commit }, mediaType) {
    commit("SET_REMOTE_PARTNER_MEDIA_FALSE", mediaType);
  },

  /* SESSION PREFERENCES */
  updatePreferenceField({ commit }, updateData) {
    commit("UPDATE_PREFERENCE_FIELD", updateData);
  },

  /* DEVICE */
  setCurrentDevice({ commit }, device) {
    commit("SET_CURRENT_DEVICE", device);
  },
};

const room = {
  namespaced: true,
  state,
  mutations,
  actions,
};

export default room;
